import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { LayoutDashboard, Package, FolderTree, ShoppingBag, Store, TicketPercent, Newspaper, FileText, Settings, ArrowLeft, BarChart3, Images, FileSignature, Wallet, Barcode, Landmark, ReceiptText, CalendarClock, CalendarDays, Bell, Trophy, ScrollText, Coins, QrCode, Vault, Megaphone, BrainCircuit, CreditCard, HelpCircle, KeyRound, Nfc, ExternalLink, Leaf } from "lucide-react";
import ErrorBoundary from "../../components/ErrorBoundary";
import CriticalPasswordGate from "../../components/admin/CriticalPasswordGate";
import { useAuth } from "../../context/AuthContext";

const MENU = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true, id: "dashboard" },
  { to: "/admin/asistan", icon: BrainCircuit, label: "AI Yönetim", id: "ai" },
  { to: "/admin/aktar-ai", icon: Leaf, label: "AktarAI Asistan", id: "aktarai" },
  { section: "E-Ticaret", id: "sec-eticaret" },
  { to: "/admin/urunler", icon: Package, label: "Ürünler", id: "products" },
  { to: "/admin/kategoriler", icon: FolderTree, label: "Kategoriler", id: "categories" },
  { to: "/admin/siparisler", icon: ShoppingBag, label: "Siparişler", id: "orders" },
  { to: "/admin/sorular", icon: HelpCircle, label: "Soru & Cevap", id: "questions" },
  { to: "/admin/stok-bildirimleri", icon: Bell, label: "Stok Bildirimleri", id: "stock-alerts" },
  { to: "/admin/teklifler", icon: FileSignature, label: "Teklif & B2B", id: "quotes" },
  { to: "/admin/pazaryerleri", icon: Store, label: "Pazaryerleri", id: "marketplaces" },
  { to: "/admin/kuponlar", icon: TicketPercent, label: "Kuponlar", id: "coupons" },
  { to: "/admin/reklamlar", icon: Megaphone, label: "Reklamlar", id: "ads" },
  { to: "/admin/blog", icon: Newspaper, label: "Blog", id: "blog" },
  { to: "/admin/ortam", icon: Images, label: "Ortam Kütüphanesi", id: "media" },
  { to: "/admin/sayfalar", icon: FileText, label: "Sayfalar", id: "pages" },
  { to: "/admin/raporlar", icon: BarChart3, label: "Raporlar", id: "reports" },
  { to: "/admin/odeme-yontemleri", icon: CreditCard, label: "Ödeme Yöntemleri", id: "payments" },
  { to: "/admin/ayarlar", icon: Settings, label: "Ayarlar", id: "settings" },
  { section: "Muhasebe", id: "sec-muhasebe" },
  { to: "/admin/finans", icon: Wallet, label: "Finans Merkezi", id: "finance" },
  { to: "/admin/takvim", icon: CalendarDays, label: "Takvim", id: "calendar" },
  { to: "/admin/cari", icon: Wallet, label: "Cari Hesaplar", id: "cari" },
  { to: "/admin/stok", icon: Barcode, label: "Stok & Barkod", id: "stock" },
  { to: "/admin/barkod-merkezi", icon: QrCode, label: "Barkod Merkezi", id: "barcodes" },
  { to: "/admin/faturalar", icon: ReceiptText, label: "Faturalar", id: "invoices" },
  { to: "/admin/en-cok-satanlar", icon: Trophy, label: "En Çok Satanlar", id: "bestsellers" },
  { to: "/admin/ata-tr-baglanti", icon: KeyRound, label: "AtaAngora.com.tr API", id: "ata-tr" },
  { to: "https://nfc-card-maker-1.emergentapps.tr/nfc/login", icon: Nfc, label: "NFC Kart", id: "nfc", external: true },
];

export default function AdminLayout() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [logoUrl, setLogoUrl] = useState("");
  const [bgUrl, setBgUrl] = useState("");
  const [snapshot, setSnapshot] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [notifOn, setNotifOn] = useState(() => localStorage.getItem("ata_notif") === "1");
  const [voiceOn, setVoiceOn] = useState(() => localStorage.getItem("ata_notif_voice") === "1");

  useEffect(() => {
    let timer;
    const check = async () => {
      try {
        const { data } = await api.get("/admin/notifications/check");
        setSnapshot(data);
        const curr = { orders: data.new_orders.count, overdue: data.overdue_installments.count, stock: data.critical_stock.count, quotes: data.pending_quotes.count };
        if (localStorage.getItem("ata_notif") === "1") {
          const stored = localStorage.getItem("ata_notif_seen");
          const prev = stored ? JSON.parse(stored) : null;
          if (prev) {
            const msgs = [];
            if (curr.orders > (prev.orders || 0)) msgs.push(`${curr.orders} bekleyen sipariş var`);
            if (curr.overdue > (prev.overdue || 0)) msgs.push(`${curr.overdue} taksitin vadesi geçti`);
            if (curr.stock > (prev.stock || 0)) msgs.push(`${curr.stock} üründe kritik stok`);
            if (curr.quotes > (prev.quotes || 0)) msgs.push(`${curr.quotes} teklif yanıt bekliyor`);
            if (msgs.length) {
              const text = msgs.join(". ");
              toast.info(text, { description: "Yönetim bildirimi" });
              if (localStorage.getItem("ata_notif_voice") === "1" && "speechSynthesis" in window) {
                const u = new SpeechSynthesisUtterance(text);
                u.lang = "tr-TR";
                window.speechSynthesis.speak(u);
              }
              if ("Notification" in window && Notification.permission === "granted") new Notification("Ata Angora Yönetim", { body: text });
            }
          }
          localStorage.setItem("ata_notif_seen", JSON.stringify(curr));
        }
      } catch { /* sessiz */ }
      timer = setTimeout(check, 45000);
    };
    check();
    return () => clearTimeout(timer);
  }, []);

  const toggleNotif = async () => {
    const next = !notifOn;
    setNotifOn(next);
    localStorage.setItem("ata_notif", next ? "1" : "0");
    if (next) {
      localStorage.removeItem("ata_notif_seen");
      if ("Notification" in window && Notification.permission === "default") await Notification.requestPermission();
      toast.success("Bildirimler açıldı");
    }
  };

  const badge = snapshot ? snapshot.new_orders.count + snapshot.overdue_installments.count + snapshot.critical_stock.count + snapshot.pending_quotes.count : 0;

  useEffect(() => {
    api.get("/settings/public").then(({ data }) => { setLogoUrl(data.logo_url || ""); setBgUrl(data.admin_bg_url || ""); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!loading && (!user || !["admin", "super_admin"].includes(user.role))) navigate("/giris");
  }, [user, loading, navigate]);

  if (loading || !user || !["admin", "super_admin"].includes(user.role)) {
    return <div className="min-h-screen flex items-center justify-center text-stone-500">Yetki kontrol ediliyor...</div>;
  }

  return (
    <div data-testid="admin-panel" className="min-h-screen bg-stone-100 flex">
      <CriticalPasswordGate />
      <aside className="w-60 bg-forest text-cream min-h-screen fixed inset-y-0 left-0 hidden md:flex flex-col">
        <div className="p-5 border-b border-white/10 flex items-center gap-2.5">
          <img src={logoUrl || "/logo.jpg"} alt="" className="h-9 w-9 rounded-full object-cover ring-2 ring-gold/50" />
          <div className="leading-tight"><p className="font-serif font-bold text-sm">Ata Angora</p><p className="text-[9px] uppercase tracking-[0.2em] text-cream/50">Yönetim Paneli</p></div>
        </div>
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {MENU.map((item) => item.section ? (
            <p key={item.id} data-testid={`admin-nav-${item.id}`} className="px-3.5 pt-4 pb-1 text-[10px] font-bold uppercase tracking-[0.18em] text-gold/70">{item.section}</p>
          ) : item.external ? (
            <a key={item.id} href={item.to} target="_blank" rel="noreferrer" data-testid={`admin-nav-${item.id}`} className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-colors text-cream/75 hover:bg-white/10 hover:text-cream">
              <item.icon className="w-[18px] h-[18px]" /> {item.label} <ExternalLink className="w-3.5 h-3.5 ml-auto opacity-50" />
            </a>
          ) : (
            <NavLink key={item.to} to={item.to} end={item.end} data-testid={`admin-nav-${item.id}`} className={({ isActive }) => `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-colors ${isActive ? "bg-gold text-stone-900" : "text-cream/75 hover:bg-white/10 hover:text-cream"}`}>
              <item.icon className="w-[18px] h-[18px]" /> {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <Link to="/" data-testid="admin-back-to-site" className="flex items-center gap-2 px-3.5 py-2.5 rounded-xl text-sm text-cream/70 hover:bg-white/10 transition-colors"><ArrowLeft className="w-4 h-4" /> Siteye Dön</Link>
        </div>
      </aside>
      <div className="flex-1 md:ml-60 min-w-0 relative" style={bgUrl ? { backgroundImage: `url(${bgUrl})`, backgroundSize: "cover", backgroundPosition: "center", backgroundAttachment: "fixed" } : undefined}>
        {bgUrl && <div data-testid="admin-bg-overlay" className="absolute inset-0 bg-stone-100/85 pointer-events-none z-0" />}
        <div className="relative z-10">
        <div className="fixed top-4 right-4 z-40">
          <button data-testid="admin-notif-bell" onClick={() => setPanelOpen((o) => !o)} className="relative bg-white border border-stone-200 shadow-md rounded-full p-2.5 hover:bg-stone-50 transition-colors" aria-label="Bildirimler">
            <Bell className="w-5 h-5 text-forest" />
            {badge > 0 && <span data-testid="admin-notif-badge" className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">{badge}</span>}
          </button>
          {panelOpen && (
            <div data-testid="admin-notif-panel" className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white rounded-2xl border border-stone-200 shadow-xl p-4 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <p className="font-semibold text-sm text-stone-900">Bildirimler</p>
                <div className="flex gap-1.5">
                  <button data-testid="notif-toggle" onClick={toggleNotif} className={`text-[11px] font-semibold px-2.5 py-1 rounded-lg transition-colors ${notifOn ? "bg-forest text-white" : "bg-stone-100 text-stone-600"}`}>{notifOn ? "Açık" : "Kapalı"}</button>
                  <button data-testid="notif-voice-toggle" onClick={() => { const v = !voiceOn; setVoiceOn(v); localStorage.setItem("ata_notif_voice", v ? "1" : "0"); }} className={`text-[11px] font-semibold px-2.5 py-1 rounded-lg transition-colors ${voiceOn ? "bg-gold text-stone-900" : "bg-stone-100 text-stone-600"}`}>Sesli {voiceOn ? "Açık" : "Kapalı"}</button>
                </div>
              </div>
              {!snapshot && <p className="text-xs text-stone-400">Kontrol ediliyor...</p>}
              {snapshot && (
                <div className="space-y-2 text-xs text-stone-700">
                  <Link to="/admin/siparisler" onClick={() => setPanelOpen(false)} data-testid="notif-link-orders" className={`block p-2.5 rounded-xl hover:bg-stone-100 transition-colors ${snapshot.new_orders.count ? "bg-amber-50" : "bg-stone-50"}`}><b>{snapshot.new_orders.count}</b> yeni / bekleyen sipariş</Link>
                  <Link to="/admin/odeme-planlari" onClick={() => setPanelOpen(false)} data-testid="notif-link-plans" className={`block p-2.5 rounded-xl hover:bg-stone-100 transition-colors ${snapshot.overdue_installments.count ? "bg-red-50 text-red-800" : "bg-stone-50"}`}><b>{snapshot.overdue_installments.count}</b> vadesi geçen taksit</Link>
                  <Link to="/admin/stok" onClick={() => setPanelOpen(false)} data-testid="notif-link-stock" className={`block p-2.5 rounded-xl hover:bg-stone-100 transition-colors ${snapshot.critical_stock.count ? "bg-amber-50" : "bg-stone-50"}`}><b>{snapshot.critical_stock.count}</b> üründe kritik stok</Link>
                  <Link to="/admin/teklifler" onClick={() => setPanelOpen(false)} data-testid="notif-link-quotes" className={`block p-2.5 rounded-xl hover:bg-stone-100 transition-colors ${snapshot.pending_quotes.count ? "bg-sky-50" : "bg-stone-50"}`}><b>{snapshot.pending_quotes.count}</b> görüntülenen teklif yanıt bekliyor</Link>
                </div>
              )}
              <p className="text-[10px] text-stone-400">Her 45 saniyede otomatik kontrol edilir. Sesli bildirim Türkçe okunur.</p>
            </div>
          )}
        </div>
        <div className="md:hidden bg-forest text-cream p-3 flex gap-2 overflow-x-auto scrollbar-none sticky top-0 z-30">
          {MENU.filter((m) => !m.section).map(({ to, label, end, id, external }) => (
            external
              ? <a key={id} href={to} target="_blank" rel="noreferrer" data-testid={`admin-nav-m-${id}`} className="px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap text-cream/75">{label}</a>
              : <NavLink key={to} to={to} end={end} data-testid={`admin-nav-m-${id}`} className={({ isActive }) => `px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap ${isActive ? "bg-gold text-stone-900" : "text-cream/75"}`}>{label}</NavLink>
          ))}
        </div>
        <main className="p-4 sm:p-8 min-w-0 overflow-x-hidden">
          <ErrorBoundary><Outlet /></ErrorBoundary>
        </main>
        </div>
      </div>
    </div>
  );
}
